import { skipToken, useQuery, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import type { AppError } from '@/infrastructure/errors/AppError';

export type Unsubscribe = () => void;

export type LiveSubscription<T> = (
  onData: (value: T) => void,
  onError: (error: AppError) => void,
) => Unsubscribe;

export interface LiveData<T> {
  readonly data: T | undefined;
  readonly error: AppError | null;
  readonly isLoading: boolean;
}

/**
 * Bridges a Firestore `onSnapshot` subscription into the TanStack Query cache.
 *
 * The query itself never fetches (`skipToken`) — the subscription owns the
 * data. Going through the cache anyway means several components can read the
 * same key without opening a second listener, and the usual cache tools keep
 * working.
 *
 * `subscribe` must be referentially stable; wrap it in `useCallback`.
 */
export function useLiveData<T>(queryKey: QueryKey, subscribe: LiveSubscription<T>): LiveData<T> {
  const queryClient = useQueryClient();
  const [error, setError] = useState<AppError | null>(null);

  const query = useQuery<T>({
    queryKey,
    queryFn: skipToken,
    staleTime: Infinity,
  });

  useEffect(() => {
    const unsubscribe = subscribe(
      (value) => {
        setError(null);
        queryClient.setQueryData<T>(queryKey, value);
      },
      (caught) => {
        setError(caught);
      },
    );

    return () => {
      unsubscribe();
      // Clearing on teardown rather than on setup: an error belonging to the
      // previous subscription must not survive into the next one.
      setError(null);
    };
    // The key is spread so a changed id re-subscribes; `queryKey` itself is a
    // new array on every render and must not be a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryClient, subscribe, ...queryKey]);

  return {
    data: query.data,
    error,
    isLoading: query.data === undefined && error === null,
  };
}
